import { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';
import ProviderQuotaView from './ProviderQuotaView';
import QuotaMap from './QuotaMap';
import QuotaStackedBar from './QuotaStackedBar';
import QuotaHeatmap from './QuotaHeatmap';
import RegionManager from './RegionManager';
import QuotaRequestHistory from './QuotaRequestHistory';
import GPUCatalogOverview from '../admin/GPUCatalogOverview';
import { QuotaData, deduplicateForAggregation } from './quotaUtils';

interface Props {
  quotas: QuotaData[];
  onRefresh: (provider?: string) => void;
  onRequestIncrease: (quota: QuotaData, groupLabel?: string, groupFamilies?: string[]) => void;
  providerStatus?: { [key: string]: { authenticated: boolean; error: string | null; has_data: boolean } };
}

const providerInfo = {
  aws: {
    name: 'Amazon Web Services',
    logo: '/icons/icons8-aws-96.svg',
    color: 'orange',
    bgColor: 'bg-orange-500',
    borderColor: 'border-orange-500',
    hoverColor: 'hover:bg-orange-600',
  },
  gcp: {
    name: 'Google Cloud Platform',
    logo: '/icons/icons8-google-cloud-96.svg',
    color: 'blue',
    bgColor: 'bg-blue-500',
    borderColor: 'border-blue-500',
    hoverColor: 'hover:bg-blue-600',
  },
  azure: {
    name: 'Microsoft Azure',
    logo: '/icons/azure.svg',
    color: 'cyan',
    bgColor: 'bg-cyan-500',
    borderColor: 'border-cyan-500',
    hoverColor: 'hover:bg-cyan-600',
  },
  // OCI and CoreWeave not yet integrated — uncomment when APIs are available
  // oci: { name: 'Oracle Cloud Infrastructure', logo: '/icons/ORCL_logo.svg', color: 'red', bgColor: 'bg-red-500', borderColor: 'border-red-500', hoverColor: 'hover:bg-red-600' },
  // coreweave: { name: 'CoreWeave', logo: '/icons/Coreweave_logo.svg', color: 'purple', bgColor: 'bg-purple-500', borderColor: 'border-purple-500', hoverColor: 'hover:bg-purple-600' },
};

export default function QuotaDashboardTabs({ quotas, onRefresh, onRequestIncrease, providerStatus = {} }: Props) {
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [regionCoordinates, setRegionCoordinates] = useState<any>({});

  useEffect(() => {
    fetchRegionCoordinates();
  }, []);

  const fetchRegionCoordinates = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${getApiUrl()}/api/quotas/regions/coordinates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRegionCoordinates(response.data.providers || {});
    } catch (err) {
      console.error('Failed to fetch region coordinates:', err);
    }
  };

  // Group quotas by provider
  const quotasByProvider = quotas.reduce((acc, quota) => {
    if (!acc[quota.provider]) {
      acc[quota.provider] = [];
    }
    acc[quota.provider].push(quota);
    return acc;
  }, {} as Record<string, QuotaData[]>);

  // Calculate stats for each provider (deduplicate AWS shared groups)
  const providerStats = Object.entries(quotasByProvider).map(([provider, providerQuotas]) => {
    const dedupedQuotas = deduplicateForAggregation(providerQuotas);
    const totalResources = dedupedQuotas.length;
    const nearLimit = dedupedQuotas.filter(q => q.utilization_percent >= 80).length;
    const atLimit = dedupedQuotas.filter(q => q.utilization_percent >= 100).length;
    const avgUtilization = totalResources > 0
      ? dedupedQuotas.reduce((sum, q) => sum + q.utilization_percent, 0) / totalResources
      : 0;

    return {
      provider,
      totalResources,
      nearLimit,
      atLimit,
      avgUtilization,
      quotas: providerQuotas,
    };
  });

  const handleProviderClick = (provider: string) => {
    setSelectedProvider(provider);
    setActiveTab('provider');
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-900 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            activeTab === 'overview'
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('map')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            activeTab === 'map'
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          Map
        </button>
        <button
          onClick={() => setActiveTab('regions')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            activeTab === 'regions'
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          Regions
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            activeTab === 'requests'
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          Requests
        </button>
        <button
          onClick={() => setActiveTab('catalog')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            activeTab === 'catalog'
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          Catalog
        </button>
        {selectedProvider && (
          <button
            onClick={() => setActiveTab('provider')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              activeTab === 'provider'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <img
                src={providerInfo[selectedProvider as keyof typeof providerInfo]?.logo}
                alt={selectedProvider}
                className="w-5 h-5"
              />
              <span>{selectedProvider.toUpperCase()}</span>
            </div>
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stacked Bar Chart */}
          {quotas.length > 0 && (
            <QuotaStackedBar quotas={quotas} onRequestIncrease={onRequestIncrease} />
          )}

          {/* Heatmap */}
          {quotas.length > 0 && (
            <QuotaHeatmap
              quotas={quotas}
              onCellClick={handleProviderClick}
              onRequestIncrease={onRequestIncrease}
              providerStatus={providerStatus}
            />
          )}

          {/* Provider Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {providerStats.map(({ provider, totalResources, nearLimit, atLimit, avgUtilization }) => {
            const info = providerInfo[provider as keyof typeof providerInfo];
            const status = providerStatus[provider];
            const isAuthenticated = status?.authenticated ?? true;

            return (
              <div
                key={provider}
                onClick={() => isAuthenticated ? handleProviderClick(provider) : null}
                className={`bg-gray-900 rounded-xl border border-gray-800 overflow-hidden transition-all ${
                  isAuthenticated
                    ? 'hover:border-gray-700 cursor-pointer hover:shadow-xl'
                    : 'opacity-50 cursor-not-allowed'
                }`}
              >
                {/* Provider Header */}
                <div className={`p-6 bg-gradient-to-r from-gray-800/50 to-gray-900/50 border-l-4 ${info.borderColor}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <img
                        src={info.logo}
                        alt={info.name}
                        className="w-12 h-12 object-contain"
                      />
                      <div>
                        <h3 className="text-xl font-bold text-white">{info.name}</h3>
                        <p className="text-sm text-gray-400">{totalResources} resources tracked</p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRefresh(provider);
                      }}
                      className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg transition-colors"
                    >
                      Refresh
                    </button>
                  </div>
                </div>

                {/* Provider Stats */}
                <div className="p-6">
                  {!isAuthenticated && (
                    <div className="mb-4 p-3 bg-red-900/20 border border-red-800 rounded-lg">
                      <p className="text-red-400 text-sm font-medium">Credentials not configured</p>
                      <p className="text-red-400/80 text-xs mt-1">{status?.error || 'Configure credentials to view quotas'}</p>
                    </div>
                  )}
                  {isAuthenticated && totalResources === 0 && (
                    <div className="mb-4 p-3 bg-gray-800/50 border border-gray-700 rounded-lg">
                      <p className="text-gray-400 text-sm">No quota data available</p>
                      <p className="text-gray-500 text-xs mt-1">Click refresh to fetch latest data</p>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">{isAuthenticated ? Math.round(avgUtilization) : '-'}%</p>
                      <p className="text-xs text-gray-400">Avg Utilization</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-yellow-500">{nearLimit}</p>
                      <p className="text-xs text-gray-400">Near Limit</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-500">{atLimit}</p>
                      <p className="text-xs text-gray-400">At Limit</p>
                    </div>
                  </div>

                  {/* Utilization Bar */}
                  <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-full ${info.bgColor} transition-all duration-500`}
                      style={{ width: `${Math.min(avgUtilization, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}

      {activeTab === 'map' && (
        <QuotaMap coordinates={regionCoordinates} />
      )}

      {activeTab === 'regions' && (
        <RegionManager />
      )}

      {activeTab === 'requests' && (
        <QuotaRequestHistory />
      )}

      {activeTab === 'catalog' && (
        <GPUCatalogOverview />
      )}

      {activeTab === 'provider' && selectedProvider && (
        <ProviderQuotaView
          provider={selectedProvider}
          quotas={quotasByProvider[selectedProvider]}
          onRefresh={() => onRefresh(selectedProvider)}
          onRequestIncrease={onRequestIncrease}
        />
      )}
    </div>
  );
}